import { useEffect } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { useAuth, type AppRole } from "@/hooks/useAuth";
import { useCorporateAccess } from "@/hooks/useCorporateAccess";
import { useBuyerHubAccess } from "@/hooks/useBuyerHubAccess";
import { buyerHubHomePath, isHubBuyerAllowedPath } from "@/services/buyerHub/hubSend";
import AccountPendingApproval from "@/components/auth/AccountPendingApproval";
import { FlexLayout, Spinner } from "@/design-system/salt";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: AppRole[];
  platformOnly?: boolean;
  corporateRoute?: boolean;
}

/** Protected Route component. */
export default function ProtectedRoute({ children, requiredRoles, platformOnly, corporateRoute }: ProtectedRouteProps) {
  const { user, loading, isActive, roles } = useAuth();
  const { isPortalUser, primaryCorporateId, hasCorporateAccess, loading: accessLoading } = useCorporateAccess();
  const { isHubBuyerUser, hubCorporateId, loading: hubLoading } = useBuyerHubAccess();
  const location = useLocation();
  const params = useParams();

  // Tag Sentry events with the active corporate so errors are filterable by corporate.
  // Must be called before any conditional returns to satisfy Rules of Hooks.
  useEffect(() => {
    if (params.id) {
      Sentry.setTag("corporate_id", params.id);
    }
    return () => {
      Sentry.setTag("corporate_id", undefined);
    };
  }, [params.id]);

  if (loading || accessLoading || hubLoading) {
    return (
      <FlexLayout align="center" justify="center" style={{minHeight: "100vh", background: "var(--salt-container-primary-background)"}}>
        <Spinner aria-label="Checking access" size="large" />
      </FlexLayout>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (!isActive) {
    return <AccountPendingApproval />;
  }

  // Hub-sent A1 users stay inside the Buyer Hub application (fail closed).
  if (isHubBuyerUser && hubCorporateId !== null && !isHubBuyerAllowedPath(location.pathname)) {
    return <Navigate to={buyerHubHomePath(hubCorporateId)} replace />;
  }

  if (requiredRoles && requiredRoles.length > 0) {
    const hasAccess = requiredRoles.some((role) => roles.includes(role));
    if (!hasAccess) {
      return <Navigate to="/corporates" replace />;
    }
  }

  // Portal users cannot access platform-level routes
  if (platformOnly && isPortalUser && primaryCorporateId) {
    return <Navigate to={`/corporate/${primaryCorporateId}/goals`} replace />;
  }

  // Corporate routes: validate portal user has access to this corporate ID
  if (corporateRoute && isPortalUser && params.id) {
    const routeCorporateId = parseInt(params.id);
    if (!hasCorporateAccess(routeCorporateId)) {
      return primaryCorporateId
        ? <Navigate to={`/corporate/${primaryCorporateId}/goals`} replace />
        : <Navigate to="/auth" replace />;
    }
  }

  return <>{children}</>;
}
